import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { VueQueryPlugin } from '@tanstack/vue-query'

// Poppins, served from our own origin (CSP font-src 'self'): the four weights the design uses.
import '@fontsource/poppins/latin-400.css'
import '@fontsource/poppins/latin-500.css'
import '@fontsource/poppins/latin-600.css'
import '@fontsource/poppins/latin-700.css'
import '@/shared/styles/main.css'
import App from '@/app/App.vue'
import { router } from '@/app/router'
import { queryClient } from '@/app/query-client'
import { bootstrapSession } from '@/app/session'

const app = createApp(App)

app.use(createPinia())
app.use(VueQueryPlugin, { queryClient })

/**
 * Dev server only: when apps/web/dev/hardcoded-session.ts exists locally (it is never
 * committed) and VITE_HARDCODED_SESSION is set, MSW answers the API in the browser from
 * the unit-test fixtures. The glob finds nothing elsewhere, so other builds are unchanged.
 */
async function startLocalMocks() {
  const state = import.meta.env.VITE_HARDCODED_SESSION
  if (!import.meta.env.DEV || (state !== 'signed-in' && state !== 'signed-out')) return
  const load = Object.values(import.meta.glob('../dev/hardcoded-session.ts'))[0]
  if (!load) return
  const { startHardcodedSession } = (await load()) as {
    startHardcodedSession: (state: 'signed-in' | 'signed-out') => Promise<void>
  }
  await startHardcodedSession(state)
}

// The session store is filled before the router resolves the first route, so the
// guard can decide without fetching. Mounting after the first route resolves keeps the
// shell from flashing on a public page before its route meta is known.
void startLocalMocks()
  .catch((error: unknown) => console.error('Local API mocks did not start', error))
  .then(bootstrapSession)
  .finally(async () => {
    app.use(router)
    await router.isReady()
    app.mount('#app')
  })
